import api from './api';

const shiftService = {
  // HRO Shift Management APIs
  
  /**
   * Create new shift
   * @param {Object} shiftData - Shift data
   * @returns {Promise}
   */
  createShift: async (shiftData) => {
    const response = await api.post('/hro/shift/create', shiftData);
    return response.data;
  },

  /**
   * Get all shifts
   * @returns {Promise}
   */
  getAllShifts: async () => {
    const response = await api.get('/hro/shift/all');
    return response.data;
  },

  /**
   * Get shift by ID
   * @param {string} shiftId - Shift ID
   * @returns {Promise}
   */
  getShiftById: async (shiftId) => {
    const response = await api.get(`/hro/shift/${shiftId}`);
    return response.data;
  },

  /**
   * Update shift
   * @param {string} shiftId - Shift ID
   * @param {Object} updateData - Update data
   * @returns {Promise}
   */
  updateShift: async (shiftId, updateData) => {
    const response = await api.patch(`/hro/shift/update/${shiftId}`, updateData);
    return response.data;
  },

  /**
   * Delete shift
   * @param {string} shiftId - Shift ID
   * @returns {Promise}
   */
  deleteShift: async (shiftId) => {
    const response = await api.delete(`/hro/shift/delete/${shiftId}`);
    return response.data;
  },

  /**
   * Assign shift to user
   * @param {string} userId - User ID
   * @param {string} shiftId - Shift ID
   * @returns {Promise}
   */
  assignShift: async (userId, shiftId) => {
    const response = await api.post('/hro/shift/assign', { userId, shiftId });
    return response.data;
  },

  /**
   * Assign shift type to HR employee with date range (dynamic assignments)
   * @param {Object} payload - Shift assignment payload
   * @returns {Promise}
   */
  assignShiftWithDateRange: async (payload) => {
    const response = await api.post('/hro/shifts/assign', payload);
    return response.data;
  },

  /**
   * Get user's current shift
   * @param {string} userId - User ID
   * @returns {Promise}
   */
  getUserShift: async (userId) => {
    const response = await api.get(`/hro/shift/user/${userId}`);
    return response.data;
  },

  /**
   * Get all HR employees with their shifts
   * @returns {Promise}
   */
  getHREmployeesWithShifts: async () => {
    const response = await api.get('/hro/shift/employees/all');
    return response.data;
  },

  /**
   * Get HR employees with current date-based shifts
   * @returns {Promise}
   */
  getHREmployeesForShiftAssignment: async () => {
    const response = await api.get('/hro/shifts/employees');
    return response.data;
  },

  /**
   * Get full shift timeline for an HR employee
   * @param {string} hrId - HR user ID
   * @returns {Promise}
   */
  getHRTimeline: async (hrId) => {
    const response = await api.get(`/hro/shifts/${hrId}`);
    return response.data;
  },

  /**
   * Get shift assignment history for user
   * @param {string} userId - User ID
   * @param {number} limit - Number of records
   * @returns {Promise}
   */
  getUserShiftHistory: async (userId, limit = 10) => {
    const response = await api.get(`/hro/shift/history/${userId}?limit=${limit}`);
    return response.data;
  },

  /**
   * Get employees by shift
   * @param {string} shiftId - Shift ID
   * @returns {Promise}
   */
  getEmployeesByShift: async (shiftId) => {
    const response = await api.get(`/hro/shift/${shiftId}/employees`);
    return response.data;
  },

  // HR APIs

  /**
   * Get own shift (HR)
   * @returns {Promise}
   */
  getMyShift: async () => {
    const response = await api.get('/hr/shifts/current');
    return response.data;
  },

  /**
   * Get next upcoming shift (HR)
   * @returns {Promise}
   */
  getMyNextShift: async () => {
    const response = await api.get('/hr/shifts/next');
    return response.data;
  },

  /**
   * Get own shift timeline (HR)
   * @returns {Promise}
   */
  getMyShiftTimeline: async () => {
    const response = await api.get('/hr/shifts/timeline');
    return response.data;
  },

  /**
   * Update shift assignment (HRO)
   * @param {string} assignmentId - Shift assignment ID
   * @param {Object} updateData - Update data (shiftType, startDate, endDate)
   * @returns {Promise}
   */
  updateShiftAssignment: async (assignmentId, updateData) => {
    const response = await api.patch(`/hro/shifts/${assignmentId}`, updateData);
    return response.data;
  },

  /**
   * Delete shift assignment (HRO)
   * @param {string} assignmentId - Shift assignment ID
   * @returns {Promise}
   */
  deleteShiftAssignment: async (assignmentId) => {
    const response = await api.delete(`/hro/shifts/${assignmentId}`);
    return response.data;
  },

  // Attendance Correction APIs (HRO)

  /**
   * Update attendance record
   * @param {string} attendanceId - Attendance record ID
   * @param {Object} updateData - Update data
   * @returns {Promise}
   */
  updateAttendanceRecord: async (attendanceId, updateData) => {
    const response = await api.patch(`/hro/attendance/update/${attendanceId}`, updateData);
    return response.data;
  },

  /**
   * Create manual attendance record
   * @param {Object} attendanceData - Attendance data
   * @returns {Promise}
   */
  createManualAttendance: async (attendanceData) => {
    const response = await api.post('/hro/attendance/manual', attendanceData);
    return response.data;
  }
};

export default shiftService;